import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../ui/Button';
import { PropertyFormTabs } from './PropertyFormTabs';
import { GeneralInfo } from './tabs/GeneralInfo';
import { PropertyDetails } from './tabs/PropertyDetails';
import { Criteria } from './tabs/Criteria';
import { Description } from './tabs/Description';
import { Photos } from './tabs/Photos';
import { Documents } from './tabs/Documents';
import { Matching } from './tabs/Matching';
import { usePropertyFormTabs } from '../../../hooks/usePropertyFormTabs';
import { PropertyFormProvider } from './contexts/PropertyFormContext';
import { PhotoProvider } from './contexts/PhotoContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { useScrollToTop } from '../../../hooks/useScrollToTop';
import { useProperties } from '../../../hooks/useProperties';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { validatePropertyData } from '../../../utils/propertyValidation';
import { usePhotoContext } from './contexts/PhotoContext';
import { TransactionTypeSelector } from './components/TransactionTypeSelector';
import { usePropertyFormContext } from './contexts/PropertyFormContext';

const PropertyFormContent: React.FC = () => {
  const navigate = useNavigate();
  const { activeTab, setActiveTab, goToNextTab } = usePropertyFormTabs();
  const { createProperty } = useProperties();
  const { user } = useAuth();
  const { formData } = usePropertyFormContext();
  const { photos } = usePhotoContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBack = () => {
    navigate('/');
  };

  useScrollToTop([activeTab]);

  const handleSave = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      console.log('Données du formulaire:', formData);
      console.log('Photos disponibles:', photos);

      // Validation des champs obligatoires
      if (!formData.propertyType) {
        alert('Veuillez sélectionner un type de bien');
        setIsSubmitting(false);
        return;
      }
      if (!formData.propertyAddress) {
        alert('Veuillez saisir l\'adresse du bien');
        setIsSubmitting(false);
        return;
      }
      if (!formData.ownerFirstName || !formData.ownerLastName) {
        alert('Veuillez saisir le nom du propriétaire');
        setIsSubmitting(false);
        return;
      }
      if (!formData.sellerPrice) {
        alert('Veuillez saisir le prix');
        setIsSubmitting(false);
        return;
      }
      if (!formData.livingArea && !formData.totalArea && !formData.landArea) {
        alert('Veuillez saisir la surface');
        setIsSubmitting(false);
        return;
      }
      const propertyTypesRequiringRooms = ['Appartement', 'Maison'];
      if (!formData.roomCount && propertyTypesRequiringRooms.includes(formData.propertyType)) {
        alert('Veuillez saisir le nombre de pièces');
        setIsSubmitting(false);
        return;
      }
      // Préparer les URLs des photos
      const photoUrls = photos
        .filter(photo => photo.forWeb && photo.progress === 100 && !photo.error)
        .map(photo => photo.serverUrl || photo.preview);
      console.log('📸 Photos à sauvegarder:', photoUrls);
      // Préparer les données pour l'API
      const rawPropertyData = {
        property_category: formData.propertyType,
        property_subtype: formData.apartmentType || formData.houseType || 'Simple',
        address: formData.propertyAddress,
        city: formData.propertyCity || formData.propertyAddress.split(',').pop()?.trim() || 'Non spécifié',
        wilaya: formData.propertyWilaya || 'Non spécifié',
        price: parseFloat(formData.salePrice?.replace(/\s/g, '')) || parseFloat(formData.sellerPrice?.replace(/\s/g, '')) || 0,
        net_price: parseFloat(formData.sellerPrice?.replace(/\s/g, '')) || 0,
        commission: parseFloat(formData.agencyCommission?.replace(/\s/g, '')) || 0,
        area: parseFloat(formData.livingArea || formData.totalArea || formData.landArea || '0'),
        living_room_area: formData.livingRoomArea ? parseFloat(formData.livingRoomArea) : null,
        usable_area: formData.usableArea ? parseFloat(formData.usableArea) : null,
        rooms: parseInt(formData.roomCount || '1'),
        bedrooms: parseInt(formData.bedroomCount || '0'),
        bathroom_count: parseInt(formData.bathroomCount || '1'),
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.totalFloors ? parseInt(formData.totalFloors) : null,
        construction_year: formData.constructionYear ? parseInt(formData.constructionYear) : null,
        general_condition: formData.condition || null,
        kitchen_type: formData.kitchenType || null,
        has_elevator: formData.hasElevator || false,
        has_parking: formData.hasParking || false,
        has_balcony: formData.hasBalcony || false,
        has_terrace: formData.hasTerrace || false,
        has_air_conditioning: formData.hasAirConditioning || false,
        has_garden: formData.hasGarden || false,
        has_pool: formData.hasPool || false,
        has_sea_view: formData.hasSeaView || false,
        has_mountain_view: formData.hasMountainView || false,
        has_garage: formData.hasGarage || false,
        has_cellar: formData.hasCellar || false,
        has_attic: formData.hasAttic || false,
        has_guardian: formData.hasGuardian || false,
        has_intercom: formData.hasIntercom || false,
        has_interphone: formData.hasInterphone || false,
        has_digicode: formData.hasDigicode || false,
        has_trustee: formData.hasTrustee || false,
        has_alarm: formData.hasAlarm || false,
        has_video_surveillance: formData.hasVideoSurveillance || false,
        has_fiber: formData.hasFiber || false,
        is_concierge: formData.is_concierge || false,
        hasManualShutter: formData.hasManualShutter || false,
        hasElectricShutter: formData.hasElectricShutter || false,
        heating_type: formData.heatingType || 'Non spécifié',
        orientation: formData.orientation || null,
        exposure: formData.exposure || null,
        floor_level: formData.floorLevel || null,
        building_condition: formData.buildingCondition || null,
        accessibility: formData.accessibility || null,
        energy_class: formData.energyClass || null,
        co2_emission: formData.co2Emission || null,
        description: formData.advertisingText || formData.showcaseText || null,
        listing_title: formData.advertisingTitle || null,
        showcase_text: formData.showcaseText || null,
        keys_location: formData.keyLocation || null,
        observations: formData.observations || null,
        owner_name: `${formData.ownerCivility || ''} ${formData.ownerFirstName || ''} ${formData.ownerLastName || ''}`.trim(),
        owner_address: formData.ownerAddress || null,
        owner_wilaya: formData.ownerWilaya || null,
        owner_city: formData.ownerCity || null,
        owner_phone: formData.ownerPhone || null,
        owner_email: formData.ownerEmail || null,
        reference_number: formData.reference || null,
        agent_id: formData.agent ? parseInt(formData.agent) : null,
        publish_dar_dz: formData.publishToDarDz || false,
        publish_ouedkniss: formData.publishToOuedkniss || false,
        photos: photoUrls,
        transactionType: formData.transactionType,
        isEstimation: formData.transactionType === 'estimation'
      };
      // Valider et normaliser les données
      const propertyData = validatePropertyData(rawPropertyData);
      console.log('Données à envoyer à l\'API:', propertyData);
      const success = await createProperty(propertyData);

      if (success) {
        // Rediriger vers la liste appropriée selon le type de transaction
        const redirectPath = {
          'estimation': '/estimations',
          'sale': '/en-vente',
          'rent': '/en-location',
          'short-rent': '/location-courte-duree'
        }[formData.transactionType || 'sale'] || '/';

        navigate(redirectPath);
      } else {
        alert('Erreur lors de la création du bien. Vérifiez votre connexion.');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Une erreur est survenue lors de la sauvegarde du bien');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralInfo onNext={goToNextTab} />;
      case 'details':
        return <PropertyDetails onNext={goToNextTab} />;
      case 'criteria':
        return <Criteria onNext={goToNextTab} />;
      case 'description':
        return <Description onNext={goToNextTab} />;
      case 'photos':
        return <Photos onNext={goToNextTab} />;
      case 'documents':
        return <Documents onSave={handleSave} />;
      case 'matching':
        return <Matching onSave={handleSave} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={handleBack}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {formData.transactionType === 'estimation' ? 'Nouvelle estimation' : 'Ajouter un bien'}
          </h1>
        </div>
        <div className="rounded-lg bg-white shadow-sm dark:bg-gray-800">
          <PropertyFormTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onSave={handleSave}
            isSubmitting={isSubmitting}
          />
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

interface PropertyFormProps {
  initialTransactionType?: string;
  isEstimation?: boolean;
}

// Composant pour le sélecteur de transaction (en dehors du contexte)
const TransactionSelector: React.FC<{
  onSelect: (type: string) => void;
  onBack: () => void;
}> = ({ onSelect, onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={onBack}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Ajouter un bien immobilier
          </h1>
        </div>
        <div className="rounded-lg bg-white p-8 shadow-sm dark:bg-gray-800">
          <div className="text-center mb-8">
            <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              Quel type de transaction souhaitez-vous créer ?
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Sélectionnez le type de transaction pour commencer
            </p>
          </div>

          <TransactionTypeSelector
            selectedType=""
            onSelect={onSelect}
          />
        </div>
      </div>
    </div>
  );
};

export const PropertyForm: React.FC<PropertyFormProps> = ({
  initialTransactionType = 'sale',
  isEstimation = false
}) => {
  const navigate = useNavigate();
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('');
  
  const transactionType = isEstimation ? 'estimation' : initialTransactionType;
  
  // Si aucun type de transaction n'est sélectionné, afficher le sélecteur
  if (!selectedTransactionType) {
    return (
      <TransactionSelector
        onSelect={(type) => setSelectedTransactionType(type)}
        onBack={() => navigate('/')}
      />
    );
  }
  
  return (
    <PropertyFormProvider initialTransactionType={selectedTransactionType}>
      <PhotoProvider>
        <DocumentProvider>
          <PropertyFormContent />
        </DocumentProvider>
      </PhotoProvider>
    </PropertyFormProvider>
  );
};
